import { AnalyticsQueryTemplate } from 'dashboard/models/analytics-query-template';
import { InquiryStatus } from 'dashboard/models/inquiry';
import {
  InquiryFilterOptions,
  MetricsGroupBy,
  MetricsInquiryMedianCompletion,
  MetricsInquiryRecentActivityVolume,
  MetricsInquiryTemplateVersionLoadedIdPayload,
  MetricsInquiryVolume,
  MetricsInquiryVolumeOverTime,
  MetricsPeriodicityPayload,
  MetricsTemplateFunnel,
  MetricsTemplateFunnelConversionOverTime,
  MetricsTemplateFunnelConversionOverTimePayload,
  MetricsTemplateFunnelFlow,
  MetricsTemplateFunnelFlowPayload,
  MetricsTemplateLoadedTemplateIdPayload,
  MetricsVerificationCheckSummary,
  MetricsVolumeSummary,
} from 'dashboard/models/metrics';
import {
  DynamicFilterOptionKey,
  InquiryGranularities,
} from 'dashboard/redux/reducers/metrics-inquiry';
import { CountryCode } from 'lib/constants/locales';
import Action from 'lib/interfaces/Action';
import { Iso8601DateString } from 'lib/types';

export interface MetricsFilterParams {
  createdAtStart: Iso8601DateString | undefined;
  createdAtEnd: Iso8601DateString | undefined;
  inquiryStatus: InquiryStatus[];
  templateIds: string[];
  groupBy: MetricsGroupBy | undefined;
  // In inquiry funnel v2 we will only select by 1 ID.
  templateOrInquiryTemplateId?: string;
  inquiryTemplateVersionId?: string;
  // This is an id representing the path that we want to show detail on.
  funnelStepIds?: string[];
  // In inquiry volume v2 we can select by multiple templates
  templateOrInquiryTemplateIds: string[];
  inquiryTagIds: string[];

  [DynamicFilterOptionKey.CountryCodes]: CountryCode[];
  [DynamicFilterOptionKey.DeviceGroups]: string[];
  [DynamicFilterOptionKey.Platforms]: string[];
}

export type MetricsActions =
  | Action<MetricsActionType.SetMetricsVolumeSummary, MetricsVolumeSummaryPayload>
  | Action<
      MetricsActionType.SetMetricsInquiryRecentActivitySummary,
      MetricsInquiryRecentActivityVolumePayload
    >
  | Action<MetricsActionType.SetMetricsTemplateVolumeSummary, MetricsTemplateVolumeSummaryPayload>
  | Action<MetricsActionType.SetMetricsTemplateFunnel, MetricsTemplateFunnelPayload>
  | Action<
      MetricsActionType.SetMetricsVerificationCheckSummary,
      MetricsVerificationCheckSummaryPayload
    >
  | Action<MetricsActionType.SetMetricsFilters, MetricsFiltersPayload>
  | Action<MetricsActionType.ResetMetricsDateFilter>
  | Action<MetricsActionType.ClearMetrics>
  | Action<
      MetricsActionType.SetMetricsTemplateFunnelConversionOverTime,
      MetricsTemplateFunnelConversionOverTimePayload
    >
  | Action<MetricsActionType.SetMetricsTemplateFunnelFlow, MetricsTemplateFunnelFlowPayload>
  | Action<MetricsActionType.SetMetricsTemplateIdLoaded, MetricsTemplateLoadedTemplateIdPayload>
  | Action<MetricsActionType.SetMetricsGranularities, MetricsPeriodicityPayload>
  | Action<MetricsActionType.SetMetricsInquiryVolume, MetricsInquiryVolumePayload>
  | Action<
      MetricsActionType.SetMetricsInquiryMedianCompletionVolume,
      MetricsInquiryMedianCompletionPayload
    >
  | Action<MetricsActionType.SetMetricsInquiryVolumeOverTime, MetricsInquiryVolumeOverTimePayload>
  | Action<
      MetricsActionType.SetMetricsInquiryTemplateVersionIdLoaded,
      MetricsInquiryTemplateVersionLoadedIdPayload
    >
  | Action<MetricsActionType.SetMetricsInquiryFilterOptions, MetricsInquiryFilterOptionsPayload>
  | Action<MetricsActionType.ResetFiltersToInitialState>
  | Action<MetricsActionType.LoadQueryTemplate, LoadQueryTemplatePayload>
  | Action<MetricsActionType.UnsetSelectedTemplate>;

export enum MetricsActionType {
  ClearMetrics = 'metrics/CLEAR_METRICS',
  LoadQueryTemplate = 'metrics/LOAD_QUERY_TEMPLATE',
  ResetFiltersToInitialState = 'metrics/RESET_TO_INITIAL_STATE',
  ResetMetricsDateFilter = 'metrics/RESET_METRICS_DATE_FILTERS',
  SetMetricsFilters = 'metrics/SET_METRICS_FILTERS',
  SetMetricsGranularities = 'metrics/SET_METRICS_PERIODICITIES',
  SetMetricsInquiryFilterOptions = 'metrics/SET_METRICS_INQUIRY_FILTER_OPTIONS',
  SetMetricsInquiryRecentActivitySummary = 'metrics/SET_METRICS_INQUIRY_RECENT_ACTIVITY_SUMMARY',
  SetMetricsInquiryTemplateVersionIdLoaded = 'metrics/SET_INQUIRY_TEMPLATE_VERSION_ID_LOADED',
  SetMetricsInquiryVolume = 'metrics/SET_METRICS_INQUIRY_VOLUME',
  SetMetricsInquiryMedianCompletionVolume = 'metrics/SET_METRICS_INQUIRY_MEDIAN_COMPLETION_VOLUME',
  SetMetricsInquiryVolumeOverTime = 'metrics/SET_METRICS_INQUIRY_VOLUME_OVER_TIME',
  SetMetricsTemplateFunnel = 'metrics/SET_METRICS_TEMPLATE_FUNNELS',
  SetMetricsTemplateFunnelConversionOverTime = 'metrics/SET_METRICS_TEMPLATE_FUNNEL_CONVERSION_OVER_TIME',
  SetMetricsTemplateFunnelFlow = 'metrics/SET_METRICS_TEMPLATE_FUNNEL_FLOW',
  SetMetricsTemplateIdLoaded = 'metrics/SET_TEMPLATE_ID_LOADED',
  SetMetricsTemplateVolumeSummary = 'metrics/SET_METRICS_TEMPLATE_VOLUME_SUMMARY',
  SetMetricsVerificationCheckSummary = 'metrics/SET_METRICS_VERIFICATION_CHECK_SUMMARY',
  SetMetricsVolumeSummary = 'metrics/SET_METRICS_INQUIRY_SUMMARY',
  UnsetSelectedTemplate = 'metrics/UNSET_SELECTED_TEMPLATE',
}

interface MetricsVolumeSummaryPayload {
  volumeSummary: MetricsVolumeSummary;
  queriedAt: Iso8601DateString;
}

export function setMetricsVolume(
  inquiryVolume: MetricsInquiryVolume,
  queriedAt: Iso8601DateString,
  filterOptions: InquiryFilterOptions,
  volumeDelta: MetricsInquiryVolume | null,
): Action<MetricsActionType.SetMetricsInquiryVolume, MetricsInquiryVolumePayload> {
  return {
    payload: {
      inquiryVolume,
      queriedAt,
      filterOptions,
      volumeDelta,
    },
    type: MetricsActionType.SetMetricsInquiryVolume,
  };
}

export function setMetricsMedianCompletionVolume(
  queriedAt: Iso8601DateString,
  completionDelta: MetricsInquiryMedianCompletion | null,
  completionSummary: MetricsInquiryMedianCompletion,
): Action<
  MetricsActionType.SetMetricsInquiryMedianCompletionVolume,
  MetricsInquiryMedianCompletionPayload
> {
  return {
    payload: {
      queriedAt,
      completionSummary,
      completionDelta,
    },
    type: MetricsActionType.SetMetricsInquiryMedianCompletionVolume,
  };
}

export function setRecentActivityVolume(
  volumeSummary: MetricsInquiryRecentActivityVolume,
  volumeDelta: MetricsInquiryRecentActivityVolume,
): Action<
  MetricsActionType.SetMetricsInquiryRecentActivitySummary,
  MetricsInquiryRecentActivityVolumePayload
> {
  return {
    payload: {
      inquiryVolume: volumeSummary,
      volumeDelta: volumeDelta,
    },
    type: MetricsActionType.SetMetricsInquiryRecentActivitySummary,
  };
}

export function setMetricsVolumeOverTime(
  inquiryVolumeOverTime: MetricsInquiryVolumeOverTime,
  queriedAt: Iso8601DateString,
): Action<MetricsActionType.SetMetricsInquiryVolumeOverTime, MetricsInquiryVolumeOverTimePayload> {
  return {
    payload: {
      inquiryVolumeOverTime,
      queriedAt,
    },
    type: MetricsActionType.SetMetricsInquiryVolumeOverTime,
  };
}

interface MetricsTemplateVolumeSummaryPayload {
  id: string;
  volumeSummary: MetricsVolumeSummary;
  queriedAt: Iso8601DateString;
}

export function setMetricsTemplateVolumeSummary(
  id: string,
  volumeSummary: MetricsVolumeSummary,
  queriedAt: Iso8601DateString,
): Action<MetricsActionType.SetMetricsTemplateVolumeSummary, MetricsTemplateVolumeSummaryPayload> {
  return {
    payload: {
      id,
      volumeSummary,
      queriedAt,
    },
    type: MetricsActionType.SetMetricsTemplateVolumeSummary,
  };
}

interface MetricsTemplateFunnelPayload {
  id: string;
  templateFunnel: MetricsTemplateFunnel;
  queriedAt: Iso8601DateString;
}

export function setMetricsTemplateIdLoaded(templateId: string) {
  return {
    payload: {
      templateId,
    },
    type: MetricsActionType.SetMetricsTemplateIdLoaded,
  };
}

export function setMetricsInquiryGranularities(granularities: InquiryGranularities) {
  return {
    payload: {
      granularities,
    },
    type: MetricsActionType.SetMetricsGranularities,
  };
}

export function setMetricsInquiryTemplateVersionIdLoaded(inquiryTemplateVersionId: string) {
  return {
    payload: {
      inquiryTemplateVersionId,
    },
    type: MetricsActionType.SetMetricsInquiryTemplateVersionIdLoaded,
  };
}

export function setMetricsTemplateFunnel(
  id: string,
  templateFunnel: MetricsTemplateFunnel,
  queriedAt: Iso8601DateString,
): Action<MetricsActionType.SetMetricsTemplateFunnel, MetricsTemplateFunnelPayload> {
  return {
    payload: {
      id,
      templateFunnel,
      queriedAt,
    },
    type: MetricsActionType.SetMetricsTemplateFunnel,
  };
}

export function setMetricsTemplateFunnelFlow(
  templateFunnels: MetricsTemplateFunnelFlow[],
  inquiryTemplateId: string,
  queriedAt: Iso8601DateString,
  updatedAt: Iso8601DateString,
): Action<MetricsActionType.SetMetricsTemplateFunnelFlow, MetricsTemplateFunnelFlowPayload> {
  return {
    payload: {
      funnelFlows: templateFunnels,
      queriedAt,
      inquiryTemplateId,
      updatedAt,
    },
    type: MetricsActionType.SetMetricsTemplateFunnelFlow,
  };
}

export function setMetricsTemplateFunnelConversionOverTime(
  templateFunnelConversionOverTime: MetricsTemplateFunnelConversionOverTime,
  queriedAt: Iso8601DateString,
): Action<
  MetricsActionType.SetMetricsTemplateFunnelConversionOverTime,
  MetricsTemplateFunnelConversionOverTimePayload
> {
  return {
    payload: {
      funnelConversionOverTime: templateFunnelConversionOverTime,
      queriedAt,
    },
    type: MetricsActionType.SetMetricsTemplateFunnelConversionOverTime,
  };
}

interface MetricsVerificationCheckSummaryPayload {
  verificationCheckSummary: MetricsVerificationCheckSummary;
  queriedAt: Iso8601DateString;
}

interface MetricsInquiryVolumePayload {
  inquiryVolume: MetricsInquiryVolume;
  queriedAt: Iso8601DateString;
  filterOptions: InquiryFilterOptions;
  volumeDelta: MetricsInquiryVolume | null;
}

interface MetricsInquiryMedianCompletionPayload {
  queriedAt: Iso8601DateString;
  completionSummary: MetricsInquiryMedianCompletion;
  completionDelta: MetricsInquiryMedianCompletion | null;
}

interface MetricsInquiryRecentActivityVolumePayload {
  inquiryVolume: MetricsInquiryRecentActivityVolume;
  volumeDelta: MetricsInquiryRecentActivityVolume;
}

interface MetricsInquiryVolumeOverTimePayload {
  inquiryVolumeOverTime: MetricsInquiryVolumeOverTime;
  queriedAt: Iso8601DateString;
}

export function setMetricsVerificationCheckSummary(
  verificationCheckSummary: MetricsVerificationCheckSummary,
  queriedAt: Iso8601DateString,
): Action<
  MetricsActionType.SetMetricsVerificationCheckSummary,
  MetricsVerificationCheckSummaryPayload
> {
  return {
    payload: {
      verificationCheckSummary,
      queriedAt,
    },
    type: MetricsActionType.SetMetricsVerificationCheckSummary,
  };
}

export function clearMetrics(): Action<MetricsActionType.ClearMetrics> {
  return {
    type: MetricsActionType.ClearMetrics,
  };
}

export function resetFiltersToInitialState(): Action<MetricsActionType.ResetFiltersToInitialState> {
  return {
    type: MetricsActionType.ResetFiltersToInitialState,
  };
}

interface MetricsFiltersPayload {
  filters: Partial<MetricsFilterParams>;
}

export function setMetricsFilters(
  filters: Partial<MetricsFilterParams>,
): Action<MetricsActionType.SetMetricsFilters, MetricsFiltersPayload> {
  return {
    payload: {
      filters,
    },
    type: MetricsActionType.SetMetricsFilters,
  };
}

export function resetMetricsDateFilter(): Action<MetricsActionType.ResetMetricsDateFilter> {
  return {
    type: MetricsActionType.ResetMetricsDateFilter,
  };
}

interface MetricsInquiryFilterOptionsPayload {
  filterOptions: InquiryFilterOptions;
}

export const setMetricInquiryFilterOptions = (
  filterOptions: InquiryFilterOptions,
): Action<
  MetricsActionType.SetMetricsInquiryFilterOptions,
  MetricsInquiryFilterOptionsPayload
> => ({
  payload: {
    filterOptions,
  },
  type: MetricsActionType.SetMetricsInquiryFilterOptions,
});

interface LoadQueryTemplatePayload {
  queryTemplate: AnalyticsQueryTemplate;
}

export const loadQueryTemplate = (
  queryTemplate: AnalyticsQueryTemplate,
): Action<MetricsActionType.LoadQueryTemplate, LoadQueryTemplatePayload> => ({
  payload: {
    queryTemplate,
  },
  type: MetricsActionType.LoadQueryTemplate,
});

export const unsetSelectedTemplate = (): Action<MetricsActionType.UnsetSelectedTemplate> => ({
  type: MetricsActionType.UnsetSelectedTemplate,
});
